import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControlLabel,
  Switch,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import axios from 'axios';
import type {
  CommentTemplate,
  GeneratedComment,
  SchoolClassOption,
  AssessmentOption,
  VariableDefinition,
} from '../../types/commentGen';

// ─── Performance colour helpers ────────────────────────────────────────────

function avgScore(vars: Record<string, any>): number | null {
  const nums = Object.values(vars).filter(
    (v) => typeof v === 'number' && !isNaN(v),
  );
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function scoreColor(avg: number | null): 'success' | 'info' | 'error' | 'default' {
  if (avg === null) return 'default';
  if (avg >= 80) return 'success';
  if (avg >= 60) return 'info';
  return 'error';
}

// ─── Variable highlighting for the template editor ─────────────────────────

function highlightVariables(text: string): React.ReactNode[] {
  const parts = text.split(/(\{\{\w+\}\})/g);
  return parts.map((part, i) =>
    /^\{\{\w+\}\}$/.test(part) ? (
      <span key={i} style={{ color: '#1976d2', fontWeight: 600 }}>
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────

const CommentGenPage: React.FC = () => {
  // ── data lists ──
  const [templates, setTemplates] = useState<CommentTemplate[]>([]);
  const [classes, setClasses] = useState<SchoolClassOption[]>([]);
  const [assessments, setAssessments] = useState<AssessmentOption[]>([]);

  // ── selection state ──
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [selectedClassId, setSelectedClassId] = useState<number | ''>('');

  // ── template editing ──
  const [templateName, setTemplateName] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateGrade, setTemplateGrade] = useState('');
  const [templateTerm, setTemplateTerm] = useState<number | ''>('');
  const [templateLanguage, setTemplateLanguage] = useState('en');
  const [variables, setVariables] = useState<VariableDefinition[]>([]);
  const [isShared, setIsShared] = useState(false);

  // ── generated comments ──
  const [comments, setComments] = useState<GeneratedComment[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [editingBody, setEditingBody] = useState('');
  const [isEditingComment, setIsEditingComment] = useState(false);

  // ── ui state ──
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);

  // ── initial data fetch ──
  useEffect(() => {
    Promise.all([
      axios.get('/api/comment-templates/'),
      axios.get('/api/school-classes/'),
      axios.get('/api/assignments/'),
    ])
      .then(([tplRes, classRes, assRes]) => {
        setTemplates(tplRes.data.results || tplRes.data);
        setClasses(classRes.data.results || classRes.data);
        setAssessments(assRes.data.results || assRes.data);
      })
      .catch(() => setError('Failed to load initial data'));
  }, []);

  // ── load template into editor ──
  const loadTemplate = useCallback(
    (id: number) => {
      const tpl = templates.find((t) => t.id === id);
      if (!tpl) return;
      setTemplateName(tpl.name);
      setTemplateBody(tpl.template_body || '');
      setTemplateSubject(tpl.subject || '');
      setTemplateGrade(tpl.grade_level || '');
      setTemplateTerm(tpl.term ?? '');
      setTemplateLanguage(tpl.language || 'en');
      setVariables(tpl.variable_config?.variables || []);
      setIsShared(tpl.is_shared);
      // load any existing generated comments
      axios
        .get(`/api/generated-comments/?template=${id}`)
        .then((r) => {
          const data = r.data.results || r.data;
          setComments(data);
          setActiveIdx(0);
          if (data.length > 0) setEditingBody(data[0].rendered_body);
        })
        .catch(() => {});
    },
    [templates],
  );

  useEffect(() => {
    if (selectedTemplateId) loadTemplate(selectedTemplateId as number);
  }, [selectedTemplateId, loadTemplate]);

  // ── save template ──
  const handleSave = async () => {
    setSaving(true);
    setError('');
    const payload = {
      name: templateName,
      template_body: templateBody,
      subject: templateSubject,
      grade_level: templateGrade,
      term: templateTerm || null,
      language: templateLanguage,
      variable_config: { variables },
      is_shared: isShared,
    };
    try {
      if (selectedTemplateId) {
        await axios.put(`/api/comment-templates/${selectedTemplateId}/`, payload);
        setToast('Template saved');
      } else {
        const res = await axios.post('/api/comment-templates/', payload);
        setSelectedTemplateId(res.data.id);
        setTemplates((prev) => [res.data, ...prev]);
        setToast('Template created');
      }
      // refresh list
      const res = await axios.get('/api/comment-templates/');
      setTemplates(res.data.results || res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // ── generate comments ──
  const handleGenerate = async () => {
    if (!selectedTemplateId || !selectedClassId) return;
    setGenerating(true);
    setError('');
    try {
      const res = await axios.post(
        `/api/comment-templates/${selectedTemplateId}/generate/`,
        { school_class_id: selectedClassId },
      );
      const data = res.data;
      setComments(data);
      setActiveIdx(0);
      if (data.length > 0) setEditingBody(data[0].rendered_body);
      setToast(`Generated ${data.length} comments`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // ── regenerate single learner ──
  const handleRegenerateLearner = async (comment: GeneratedComment) => {
    if (!selectedTemplateId || !selectedClassId) return;
    try {
      const res = await axios.post(
        `/api/comment-templates/${selectedTemplateId}/generate/${comment.learner}/`,
        { school_class_id: selectedClassId },
      );
      setComments((prev) =>
        prev.map((c) => (c.id === comment.id ? res.data : c)),
      );
      setEditingBody(res.data.rendered_body);
      setToast('Comment regenerated');
    } catch {
      setError('Failed to regenerate comment');
    }
  };

  // ── save edited comment ──
  const handleSaveComment = async (comment: GeneratedComment) => {
    try {
      const res = await axios.patch(`/api/generated-comments/${comment.id}/`, {
        rendered_body: editingBody,
      });
      setComments((prev) =>
        prev.map((c) => (c.id === comment.id ? res.data : c)),
      );
      setIsEditingComment(false);
      setToast('Comment saved');
    } catch {
      setError('Failed to save comment');
    }
  };

  // ── copy to clipboard ──
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setToast('Copied to clipboard');
  };

  // ── export ──
  const handleExport = async (format: 'csv' | 'text') => {
    try {
      const res = await axios.post(
        '/api/generated-comments/export/',
        { format, comment_ids: comments.map((c) => c.id) },
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `comments.${format === 'csv' ? 'csv' : 'txt'}`;
      link.click();
      window.URL.revokeObjectURL(url);
      setToast(`Exported ${comments.length} comments`);
    } catch {
      setError('Export failed');
    }
  };

  // ── variable config helpers ──
  const addVariable = () => {
    setVariables((prev) => [
      ...prev,
      { key: '', label: '', source: 'assessment', score_field: 'percentage' },
    ]);
  };

  const updateVariable = (idx: number, updates: Partial<VariableDefinition>) => {
    setVariables((prev) =>
      prev.map((v, i) => (i === idx ? { ...v, ...updates } : v)),
    );
  };

  const removeVariable = (idx: number) => {
    setVariables((prev) => prev.filter((_, i) => i !== idx));
  };

  // current comment
  const currentComment = comments[activeIdx] || null;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Comment Generator
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generate personalised report comments from assessment data using templates.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* ── Template selector + class selector ── */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Template"
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(Number(e.target.value) || '')}
            >
              <MenuItem value="">
                <em>Select a template</em>
              </MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Class"
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(Number(e.target.value) || '')}
            >
              <MenuItem value="">
                <em>Select a class</em>
              </MenuItem>
              {classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                onClick={handleGenerate}
                disabled={!selectedTemplateId || !selectedClassId || generating}
              >
                Generate
              </Button>
              <Button
                variant="outlined"
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!templateName || saving}
              >
                Save Template
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedTemplateId('');
                  setTemplateName('');
                  setTemplateBody('');
                  setVariables([]);
                  setComments([]);
                  setNewTemplateOpen(true);
                }}
              >
                New
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* ── Left: Template Editor ── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Template
            </Typography>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                fullWidth
              />
              <Grid container spacing={1}>
                <Grid size={{ xs: 4 }}>
                  <TextField
                    size="small"
                    label="Subject"
                    value={templateSubject}
                    onChange={(e) => setTemplateSubject(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <TextField
                    size="small"
                    label="Grade"
                    value={templateGrade}
                    onChange={(e) => setTemplateGrade(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <TextField
                    select
                    size="small"
                    label="Term"
                    value={templateTerm}
                    onChange={(e) =>
                      setTemplateTerm(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    fullWidth
                  >
                    <MenuItem value="">-</MenuItem>
                    {[1, 2, 3, 4].map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <TextField
                    select
                    size="small"
                    label="Language"
                    value={templateLanguage}
                    onChange={(e) => setTemplateLanguage(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="af">Afrikaans</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              <FormControlLabel
                control={
                  <Switch
                    checked={isShared}
                    onChange={(e) => setIsShared(e.target.checked)}
                    size="small"
                  />
                }
                label="Share with school"
              />

              {/* Template body editor with preview overlay */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Template Body
                </Typography>
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    multiline
                    fullWidth
                    minRows={10}
                    maxRows={20}
                    value={templateBody}
                    onChange={(e) => setTemplateBody(e.target.value)}
                    placeholder="Enter template with {{variable}} placeholders..."
                    sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  />
                </Box>
                {/* Preview of detected placeholders */}
                {templateBody && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    {Array.from(new Set(
                      (templateBody.match(/\{\{(\w+)\}\}/g) || []).map((m) =>
                        m.replace(/[{}]/g, ''),
                      ),
                    )).map((key) => (
                      <Chip key={key} label={`{{${key}}}`} size="small" color="primary" variant="outlined" />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Paper>

          {/* Variable Configuration */}
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Variables</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addVariable}>
                Add Variable
              </Button>
            </Stack>
            {variables.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No variables configured. Add variables to map template placeholders to assessment data.
              </Typography>
            )}
            <Stack spacing={1.5}>
              {variables.map((v, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid size={{ xs: 3 }}>
                      <TextField
                        size="small"
                        label="Key"
                        value={v.key}
                        onChange={(e) => updateVariable(idx, { key: e.target.value })}
                        fullWidth
                        placeholder="e.g. sql_score"
                      />
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <TextField
                        size="small"
                        label="Label"
                        value={v.label}
                        onChange={(e) => updateVariable(idx, { label: e.target.value })}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <TextField
                        select
                        size="small"
                        label="Source"
                        value={v.source}
                        onChange={(e) =>
                          updateVariable(idx, {
                            source: e.target.value as 'assessment' | 'computed',
                          })
                        }
                        fullWidth
                      >
                        <MenuItem value="assessment">Assessment</MenuItem>
                        <MenuItem value="computed">Computed</MenuItem>
                      </TextField>
                    </Grid>
                    {v.source === 'assessment' && (
                      <>
                        <Grid size={{ xs: 2 }}>
                          <TextField
                            select
                            size="small"
                            label="Assessment"
                            value={v.assessment_id || ''}
                            onChange={(e) =>
                              updateVariable(idx, { assessment_id: Number(e.target.value) })
                            }
                            fullWidth
                          >
                            {assessments.map((a) => (
                              <MenuItem key={a.id} value={a.id}>
                                {a.title}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 2 }}>
                          <TextField
                            select
                            size="small"
                            label="Field"
                            value={v.score_field || 'percentage'}
                            onChange={(e) => updateVariable(idx, { score_field: e.target.value })}
                            fullWidth
                          >
                            <MenuItem value="percentage">Percentage</MenuItem>
                            <MenuItem value="raw_score">Raw Score</MenuItem>
                          </TextField>
                        </Grid>
                      </>
                    )}
                    {v.source === 'computed' && (
                      <Grid size={{ xs: 2 }}>
                        <TextField
                          select
                          size="small"
                          label="Rule"
                          value={v.rule || ''}
                          onChange={(e) =>
                            updateVariable(idx, {
                              rule: e.target.value as VariableDefinition['rule'],
                            })
                          }
                          fullWidth
                        >
                          <MenuItem value="threshold">Threshold</MenuItem>
                          <MenuItem value="list_weak">List Weak</MenuItem>
                          <MenuItem value="list_strong">List Strong</MenuItem>
                          <MenuItem value="conditional">Conditional</MenuItem>
                        </TextField>
                      </Grid>
                    )}
                  </Grid>
                  <Box sx={{ mt: 0.5, textAlign: 'right' }}>
                    <Button size="small" color="error" onClick={() => removeVariable(idx)}>
                      Remove
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* ── Right: Comment Review Panel ── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">
                Generated Comments ({comments.length})
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('csv')}
                  disabled={comments.length === 0}
                >
                  CSV
                </Button>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('text')}
                  disabled={comments.length === 0}
                >
                  Text
                </Button>
              </Stack>
            </Stack>

            {/* Learner pill bar */}
            {comments.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                {comments.map((c, idx) => {
                  const avg = avgScore(c.resolved_vars);
                  return (
                    <Chip
                      key={c.id}
                      label={c.learner_first_name}
                      size="small"
                      color={scoreColor(avg)}
                      variant={idx === activeIdx ? 'filled' : 'outlined'}
                      onClick={() => {
                        setActiveIdx(idx);
                        setEditingBody(c.rendered_body);
                        setIsEditingComment(false);
                      }}
                    />
                  );
                })}
              </Stack>
            )}

            {/* Active comment card */}
            {currentComment ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {currentComment.learner_name}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    {currentComment.is_edited && (
                      <Chip label="edited" size="small" color="warning" variant="outlined" />
                    )}
                    <Tooltip title="Copy to clipboard">
                      <IconButton size="small" onClick={() => handleCopy(currentComment.rendered_body)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Regenerate this learner">
                      <IconButton size="small" onClick={() => handleRegenerateLearner(currentComment)}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                {/* Score badges */}
                <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.entries(currentComment.resolved_vars)
                    .filter(([, v]) => typeof v === 'number')
                    .map(([key, val]) => (
                      <Chip
                        key={key}
                        label={`${key}: ${val}`}
                        size="small"
                        color={scoreColor(val as number)}
                        variant="outlined"
                      />
                    ))}
                </Stack>

                <Divider sx={{ mb: 1.5 }} />

                {/* Comment body — editable or read-only */}
                {isEditingComment ? (
                  <>
                    <TextField
                      multiline
                      fullWidth
                      minRows={6}
                      maxRows={16}
                      value={editingBody}
                      onChange={(e) => setEditingBody(e.target.value)}
                      sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    />
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckIcon />}
                        onClick={() => handleSaveComment(currentComment)}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setEditingBody(currentComment.rendered_body);
                          setIsEditingComment(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </>
                ) : (
                  <Box
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      p: 1,
                      borderRadius: 1,
                    }}
                    onClick={() => setIsEditingComment(true)}
                  >
                    {currentComment.rendered_body}
                    <Tooltip title="Click to edit">
                      <EditIcon sx={{ fontSize: 14, ml: 1, color: 'text.secondary', verticalAlign: 'middle' }} />
                    </Tooltip>
                  </Box>
                )}
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {loading || generating
                  ? 'Loading...'
                  : 'Select a template and class, then click Generate.'}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── New template dialog ── */}
      <Dialog open={newTemplateOpen} onClose={() => setNewTemplateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Comment Template</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Template Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Subject"
              value={templateSubject}
              onChange={(e) => setTemplateSubject(e.target.value)}
              fullWidth
            />
            <TextField
              label="Grade Level"
              value={templateGrade}
              onChange={(e) => setTemplateGrade(e.target.value)}
              fullWidth
              placeholder="e.g. Grade 11"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTemplateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setNewTemplateOpen(false);
              setSelectedTemplateId('');
            }}
            disabled={!templateName}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
      />
    </Box>
  );
};

export default CommentGenPage;
